import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { cn } from '@/lib/utils';
import type { RecruitOutcome } from '@/schemas/character';
import type { FollowerTemplate } from '@/schemas';
import type { StatId } from '@/schemas/stats';

/**
 * **FI-76 (2026-05-22)** — stile card-like per le row 10+/7-9 (coerente con
 * `MoveChoiceGroupsField`). Click ovunque sulla label → triggera il radio.
 */
const RECRUIT_ROW_BASE_CLASS =
  'border-rule bg-paper-bright flex cursor-pointer items-start gap-2 border px-[0.65em] py-[0.4em] transition-[background-color,border-color] duration-150 hover:border-ink active:bg-paper-soft';
const RECRUIT_ROW_SELECTED_CLASS = 'border-ink bg-paper-soft';

/**
 * **D8a — `MoveRecruitOutcomeField`**: selettore dell'esito del tiro al
 * reclutamento per le mosse-source di Follower con `FollowerTemplate.recruitRoll`
 * (Druido *Supporto Elementale*, Sciamano *Animare Zombi*).
 *
 * Manuale rr. 3174-3190 e 5599-5618: il giocatore tira il dado al tavolo
 * (`d6+d6+modificatore di stat`); l'esito determina **quante peculiarità**
 * può selezionare dalla lista (`onSuccess` per 10+, `onPartial` per 7-9).
 *
 * Il fallimento (6-) non concede il follower → la mossa stessa non viene
 * presa o resta description-only. Questa UI gestisce solo i 2 esiti positivi.
 *
 * Quando l'utente cambia l'esito, le selezioni del gruppo `main` vengono
 * **resettate** (responsabilità del consumer via `updateMoveRecruitOutcome`)
 * perché il `pickCount` cambia.
 */

const STAT_LABEL: Record<StatId, string> = {
  forza: 'Forza',
  destrezza: 'Destrezza',
  costituzione: 'Costituzione',
  intelligenza: 'Intelligenza',
  saggezza: 'Saggezza',
  carisma: 'Carisma',
};

interface MoveRecruitOutcomeFieldProps {
  moveId: string;
  recruitRoll: NonNullable<FollowerTemplate['recruitRoll']>;
  value: RecruitOutcome | undefined;
  onChange: (next: RecruitOutcome) => void;
}

export function MoveRecruitOutcomeField({
  moveId,
  recruitRoll,
  value,
  onChange,
}: MoveRecruitOutcomeFieldProps) {
  const statLabel = STAT_LABEL[recruitRoll.stat];
  const successId = `recruit-${moveId}-10plus`;
  const partialId = `recruit-${moveId}-7-9`;

  return (
    <div className="grid gap-2 rounded-md border border-amber-200 bg-amber-50/50 p-3 dark:border-amber-900/50 dark:bg-amber-950/20">
      <div className="grid gap-0.5">
        <p className="text-sm font-medium">Esito del tiro al reclutamento</p>
        <p className="text-xs text-muted-foreground">
          Tira <strong>+{statLabel}</strong> al tavolo, poi dichiara qui l&apos;esito. Il numero di
          peculiarità selezionabili dipende dal risultato.
        </p>
      </div>
      <RadioGroup
        value={value ?? ''}
        onValueChange={(v) => onChange(v as RecruitOutcome)}
        className="gap-1"
      >
        <label
          htmlFor={successId}
          className={cn(RECRUIT_ROW_BASE_CLASS, value === '10+' && RECRUIT_ROW_SELECTED_CLASS)}
        >
          <RadioGroupItem value="10+" id={successId} className="mt-1" />
          <span className="text-sm leading-snug">
            <span className="font-medium">10+</span>
            <span className="text-muted-foreground">
              {' '}
              · scegli {recruitRoll.onSuccess.choicesCount} peculiarità
            </span>
          </span>
        </label>
        <label
          htmlFor={partialId}
          className={cn(RECRUIT_ROW_BASE_CLASS, value === '7-9' && RECRUIT_ROW_SELECTED_CLASS)}
        >
          <RadioGroupItem value="7-9" id={partialId} className="mt-1" />
          <span className="text-sm leading-snug">
            <span className="font-medium">7-9</span>
            <span className="text-muted-foreground">
              {' '}
              · scegli {recruitRoll.onPartial.choicesCount} peculiarità
            </span>
          </span>
        </label>
      </RadioGroup>
    </div>
  );
}
